import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Bell, Check, CheckCheck, Trash2, MessageSquare, Calendar, Clock, FolderKanban, Users, Palmtree } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const notificationIcons = {
    task_assigned: CheckCheck,
    task_status: Clock,
    task_comment: MessageSquare,
    project_added: FolderKanban,
    milestone_completed: Calendar,
    vacation_request: Palmtree,
    vacation_approved: Palmtree,
    vacation_rejected: Palmtree,
};

const NotificationsPage = () => {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchNotifications = async () => {
        try {
            const response = await api.get('/notifications');
            setNotifications(response.data);
        } catch (error) {
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotifications();
    }, []);

    const handleMarkRead = async (notificationId) => {
        try {
            await api.put(`/notifications/${notificationId}/read`);
            setNotifications(prev => 
                prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
            );
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await api.put('/notifications/read-all');
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            toast.success('Alle gelesen');
        } catch (error) {
            toast.error('Fehler');
        }
    };

    const handleClick = (notification) => {
        handleMarkRead(notification.id);
        if (notification.link) {
            navigate(notification.link);
        }
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8" data-testid="notifications-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        🔔 Benachrichtigungen
                    </h1>
                    <p className="text-muted-foreground">
                        {unreadCount > 0 ? `${unreadCount} ungelesen` : 'Alles gelesen'}
                    </p>
                </div>
                {unreadCount > 0 && (
                    <Button variant="outline" onClick={handleMarkAllRead} data-testid="mark-all-read">
                        <CheckCheck className="w-4 h-4 mr-2" />
                        Alle als gelesen markieren
                    </Button>
                )}
            </div>

            {/* Notifications List */}
            <Card className="shadow-soft">
                <CardContent className="p-0">
                    {notifications.length > 0 ? (
                        <div className="divide-y divide-border/50">
                            {notifications.map(notification => {
                                const Icon = notificationIcons[notification.type] || Bell;
                                
                                return (
                                    <motion.div
                                        key={notification.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className={`flex items-start gap-4 p-4 cursor-pointer transition-colors hover:bg-muted/50
                                            ${!notification.is_read ? 'bg-primary/5' : ''}`}
                                        onClick={() => handleClick(notification)}
                                        data-testid={`notification-${notification.id}`}
                                    >
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                                            ${!notification.is_read ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className={`font-medium ${!notification.is_read ? '' : 'text-muted-foreground'}`}>
                                                    {notification.title}
                                                </p>
                                                {!notification.is_read && (
                                                    <Badge className="bg-primary/20 text-primary text-[10px]">Neu</Badge>
                                                )}
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-0.5">{notification.message}</p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {format(new Date(notification.created_at), 'PPp', { locale: de })}
                                            </p>
                                        </div>
                                        {!notification.is_read && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleMarkRead(notification.id);
                                                }}
                                                data-testid={`mark-read-${notification.id}`}
                                            >
                                                <Check className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </motion.div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-muted-foreground">
                            <Bell className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>Keine Benachrichtigungen</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default NotificationsPage;
